import { useCallback, useState } from 'react';

import { type ComposerDestination, fetchCommunityAnnouncementConfirmation } from '@shared/dream-components';
import type { JSONContent } from '@tiptap/core';

import type { AnnouncementConfirmationResponse } from '~/services/api/community/getAnnouncementConfirmation.server';

import useRequestAnnouncementConfirmation from '../../_hooks/useRequestAnnouncementConfirmation';

interface UseAnnouncementConfirmationParams {
  destination: ComposerDestination | null;
  editPostId: string | null;
  submitPost: (content: JSONContent, scheduledAt?: Date) => void;
  onError: () => void;
  hasSubmitError: boolean;
}

// Asks before a post with new @everyone or @badge mentions goes out, showing how many members they notify.
const useAnnouncementConfirmation = ({
  destination,
  editPostId,
  submitPost,
  onError,
  hasSubmitError,
}: UseAnnouncementConfirmationParams) => {
  const requestConfirmation = useRequestAnnouncementConfirmation();
  const [isChecking, setIsChecking] = useState(false);
  // Kept after the dialog closes so its content stays during the fade-out.
  const [pending, setPending] = useState<{
    content: JSONContent;
    scheduledAt?: Date;
    confirmation: AnnouncementConfirmationResponse;
  } | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const submitWithConfirmation = useCallback(
    async (content: JSONContent, scheduledAt?: Date) => {
      const confirmationRequest = fetchCommunityAnnouncementConfirmation(content, ({ everyone, badgeIds }) =>
        requestConfirmation({
          spaceSlug: destination?.kind === 'channel' ? destination.space.slug : null,
          everyone,
          badgeIds,
          postId: editPostId,
        }),
      );
      if (!confirmationRequest) {
        submitPost(content, scheduledAt);
        return;
      }

      setIsChecking(true);
      try {
        const confirmation = await confirmationRequest;
        if (!confirmation) {
          submitPost(content, scheduledAt);
          return;
        }
        setPending({ content, scheduledAt, confirmation });
        setIsOpen(true);
      } catch {
        onError();
      } finally {
        setIsChecking(false);
      }
    },
    [destination, editPostId, onError, requestConfirmation, submitPost],
  );

  return {
    isChecking,
    /** Whether the post being confirmed has a publish time, which the confirmation words for. */
    isScheduling: Boolean(pending?.scheduledAt),
    submitWithConfirmation,
    dialogProps: {
      // Stays open while the post sends, so a second click can't send it again.
      // A failed send closes it, because the composer behind it or a toast shows the error.
      isOpen: isOpen && !(isSending && hasSubmitError),
      isLoading: isSending,
      confirmation: pending?.confirmation ?? null,
      onConfirm: () => {
        if (isSending || !pending) return;
        setIsSending(true);
        submitPost(pending.content, pending.scheduledAt);
      },
      onCancel: () => setIsOpen(false),
      afterLeave: () => {
        setIsOpen(false);
        setIsSending(false);
        setPending(null);
      },
    },
  };
};

export default useAnnouncementConfirmation;
